import { Grid } from '@material-ui/core'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import Payment from '@material-ui/icons/Payment'
import ReportOutlined from '@material-ui/icons/ReportOutlined'
import WarningIcon from '@material-ui/icons/Warning'
import { get } from 'lodash'
import { TextField } from 'react-admin'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { pluralize } from 'shared/helpers/utils'
import { getJobConsoleStatusDetail, JobStatusDetail as JobStatusDetailEnum } from 'shared/models/job'

import {
  IssueStatus,
  JobStatusDetail,
  JobStatusDetailIconColorMap,
  PendingIconObj,
} from '../../../helpers/constants'
import { Job } from '../../../models/jobs'
import { CustomReferenceField } from '../../Common/CustomReferenceFields'
import { CalloutField, LabeledTypographyField } from '../../Fields'

interface JobDetailsStatusDetailProps {
  record: Job
  classes?: any
  reviewForApprovalResponse?: any
}

export function JobDetailsStatusDetail({
  record,
  classes,
  reviewForApprovalResponse,
}: JobDetailsStatusDetailProps) {
  const unresolvedIssuesCount =
    record && record.issues
      ? record.issues.filter((issue: any) => issue.status !== IssueStatus.Resolved).length
      : 0

  return (
    <>
      <FlexBox justifyContent="space-between" alignItems="center">
        {!(record?.status_detail === JobStatusDetail.RecoveryNeeded) && (
          <JobStatusCalloutField record={record} />
        )}
        {record?.status_detail === JobStatusDetail.Recovered && (
          <CustomReferenceField
            record={record}
            source="recovery_job"
            sortable={false}
            label="Recovery Job"
            baseUrl="/jobs"
            representationFunction={() => record?.recovery_job?.human_id}
          />
        )}
      </FlexBox>
      {record?.abort_reason && <TextField record={record} source="abort_reason" />}
      {record?.abort_cancel_detail && <TextField record={record} source="abort_cancel_detail" />}
      {[JobStatusDetail.RequestedCancellation, JobStatusDetail.Canceled].includes(record?.status_detail) && (
        <Grid container item direction="column">
          <Grid item>
            <LabeledTypographyField
              variant="subtitle1"
              label="Late cancellation"
              typographyProps={{
                className:
                  record?.status_detail === JobStatusDetail.RequestedCancellation &&
                  classes.lateCancellationRequest,
              }}
              value={record?.is_request_cancellation_late ? 'Yes' : 'No'}
            />
          </Grid>
          {record?.status_detail === JobStatusDetail.Canceled && (
            <Grid item>
              <LabeledTypographyField
                variant="subtitle1"
                label="Cancel reason"
                value={record?.cancel_reason}
              />
            </Grid>
          )}
          <Grid item>
            <LabeledTypographyField
              variant="subtitle1"
              label="Cancel request reason"
              value={record?.cancel_request_reason}
            />
          </Grid>
          <Grid item>
            <LabeledTypographyField
              variant="subtitle1"
              label="Cancel request description"
              value={record?.cancel_request_detail}
            />
          </Grid>
        </Grid>
      )}
      {record?.status_detail === JobStatusDetail.RecoveryNeeded && record?.recovery_job && (
        <FlexBox justifyContent="space-between" alignItems="center">
          <CalloutField
            icon={WarningIcon}
            iconProps={{ style: { color: 'orange' } }}
            label="Recovery needed"
          />
          <CustomReferenceField
            record={record}
            source="recovery_job"
            sortable={false}
            label="Recovery Job"
            baseUrl="/jobs"
            representationFunction={() => record?.recovery_job?.human_id}
          />
        </FlexBox>
      )}
      {record?.status_detail === JobStatusDetail.RecoveryNeeded &&
        !record?.recovery_job &&
        record?.recovery_load && (
          <FlexBox justifyContent="space-between" alignItems="center">
            <CalloutField
              icon={WarningIcon}
              iconProps={{ style: { color: 'orange' } }}
              label="Recovery needed"
            />
            <CustomReferenceField
              record={record}
              source="recovery_load"
              sortable={false}
              label="Recovery Load"
              baseUrl="/loads"
              representationFunction={() => 'new load'}
            />
          </FlexBox>
        )}
      {record?.recovery_reason && (
        <Grid item>
          <LabeledTypographyField
            variant="subtitle1"
            label="Recovery Reason"
            value={record?.recovery_reason}
          />
        </Grid>
      )}
      {record?.payment_in_dispute && (
        <Grid item>
          <CalloutField icon={Payment} iconProps={{ color: 'error' }} label="Payment in Dispute" />
        </Grid>
      )}
      {record?.hazmat_detected && !reviewForApprovalResponse?.auto_approval_failure_flags && (
        <Grid item>
          <CalloutField icon={ReportOutlined} iconProps={{ color: 'error' }} label="Hazmat detected" />
        </Grid>
      )}
      {unresolvedIssuesCount > 0 && (
        <Grid item>
          <CalloutField
            icon={WarningIcon}
            iconProps={{ style: { color: 'orange' } }}
            label={pluralize(unresolvedIssuesCount, 'Issue')}
          />
        </Grid>
      )}
      {record?.is_driver_suspended && (
        <Grid item>
          <CalloutField icon={ErrorOutlineIcon} iconProps={{ color: 'error' }} label="Suspended driver" />
        </Grid>
      )}
    </>
  )
}

const JobStatusCalloutField = ({ record }: any) => {
  const statusDetail = record.status_detail
  const mapVal = get(JobStatusDetailIconColorMap, statusDetail, PendingIconObj)
  let label = <span>{getJobConsoleStatusDetail(statusDetail as JobStatusDetailEnum)}</span>
  if (statusDetail === JobStatusDetail.JobApproved && record?.is_auto_approved === true) {
    label = <span>Auto-approved</span>
  } else if (
    statusDetail === JobStatusDetail.ReviewingRateConfirmation &&
    record?.is_auto_approved === false
  ) {
    label = <span>{label}</span>
  }
  return <CalloutField icon={mapVal.icon} iconProps={{ style: { color: mapVal.color } }} label={label} />
}
